/**
 * Проверка российских реквизитов по контрольным суммам.
 *
 * Смысл не в формальной валидации, а в том, чтобы поймать опечатку в момент
 * ввода. Неверный ИНН контрагента всплывёт позже — при отправке УПД оператору
 * ЭДО, когда документ уже выставлен и его надо переделывать.
 */
const OK = { valid: true };
const fail = (error) => ({ valid: false, error });
function isDigits(value) {
    return /^\d+$/.test(value);
}
function checksum(digits, weights) {
    let total = 0;
    for (let i = 0; i < weights.length; i += 1) {
        total += weights[i] * Number(digits[i]);
    }
    return (total % 11) % 10;
}
/**
 * ИНН: 10 знаков у юрлица, 12 у ИП и физлица.
 */
export function checkInn(inn) {
    const value = inn.trim();
    if (!value)
        return fail('ИНН не заполнен');
    if (!isDigits(value))
        return fail('ИНН должен состоять только из цифр');
    if (value.length === 10) {
        const expected = checksum(value, [2, 4, 10, 3, 5, 9, 4, 6, 8]);
        return expected === Number(value[9]) ? OK : fail('Неверная контрольная сумма ИНН');
    }
    if (value.length === 12) {
        const first = checksum(value, [7, 2, 4, 10, 3, 5, 9, 4, 6, 8]);
        const second = checksum(value, [3, 7, 2, 4, 10, 3, 5, 9, 4, 6, 8]);
        return first === Number(value[10]) && second === Number(value[11])
            ? OK
            : fail('Неверная контрольная сумма ИНН');
    }
    return fail('ИНН должен содержать 10 цифр (организация) или 12 (ИП)');
}
/**
 * КПП: 9 знаков. Пятый и шестой символ — код причины постановки на учёт,
 * у иностранных организаций там могут быть латинские буквы A–Z.
 */
export function checkKpp(kpp) {
    const value = kpp.trim();
    if (!value)
        return fail('КПП не заполнен');
    if (value.length !== 9)
        return fail('КПП должен содержать 9 знаков');
    if (!/^\d{4}[\dA-Z]{2}\d{3}$/.test(value)) {
        return fail('Неверный формат КПП');
    }
    return OK;
}
/**
 * ОГРН (13 знаков) и ОГРНИП (15 знаков).
 * Контрольная цифра — остаток от деления на 11 или 13 соответственно.
 */
export function checkOgrn(ogrn) {
    const value = ogrn.trim();
    if (!value)
        return fail('ОГРН не заполнен');
    if (!isDigits(value))
        return fail('ОГРН должен состоять только из цифр');
    if (value.length === 13) {
        const base = BigInt(value.slice(0, 12));
        const control = Number(base % 11n) % 10;
        return control === Number(value[12]) ? OK : fail('Неверная контрольная сумма ОГРН');
    }
    if (value.length === 15) {
        const base = BigInt(value.slice(0, 14));
        const control = Number(base % 13n) % 10;
        return control === Number(value[14]) ? OK : fail('Неверная контрольная сумма ОГРНИП');
    }
    return fail('ОГРН должен содержать 13 цифр (организация) или 15 (ИП)');
}
/** БИК: 9 цифр, начинается с 04 (российские банки). */
export function checkBik(bik) {
    const value = bik.trim();
    if (!value)
        return fail('БИК не заполнен');
    if (!isDigits(value) || value.length !== 9)
        return fail('БИК должен содержать 9 цифр');
    if (!value.startsWith('04'))
        return fail('БИК российского банка начинается с «04»');
    return OK;
}
// 23 позиции: 3 разряда из БИК + 20 цифр счёта. Коэффициенты 7-1-3 по кругу.
const ACCOUNT_WEIGHTS = Array.from({ length: 23 }, (_, i) => [7, 1, 3][i % 3]);
/**
 * Расчётный счёт: 20 цифр, контролируется вместе с БИК.
 * Проверка «на месте» ловит переставленные цифры, которые иначе всплывут
 * только когда банк вернёт платёж.
 */
export function checkBankAccount(account, bik) {
    const acc = account.trim();
    const bikValue = bik.trim();
    if (!acc)
        return fail('Расчётный счёт не заполнен');
    if (!isDigits(acc) || acc.length !== 20) {
        return fail('Расчётный счёт должен содержать 20 цифр');
    }
    const bikCheck = checkBik(bikValue);
    if (!bikCheck.valid)
        return fail('Для проверки счёта нужен корректный БИК');
    // Корреспондентский счёт проверяется с префиксом «0» + 5–6 разряды БИК,
    // расчётный — с 7–9 разрядами БИК.
    const isCorrespondent = acc.startsWith('301');
    const prefix = isCorrespondent
        ? `0${bikValue.slice(4, 6)}`
        : bikValue.slice(6, 9);
    const combined = prefix + acc;
    let total = 0;
    for (let i = 0; i < combined.length; i += 1) {
        total += (ACCOUNT_WEIGHTS[i] * Number(combined[i])) % 10;
    }
    return total % 10 === 0 ? OK : fail('Неверная контрольная сумма расчётного счёта');
}
/** Нормализация: убрать пробелы и прочие разделители из введённого реквизита. */
export function normalizeRequisite(value) {
    return value.replace(/[\s -]/g, '');
}
//# sourceMappingURL=requisites.js.map